import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ShopRes from "types/res/shop/ShopRes";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import {
  addNotification,
  deleteNotification,
  fetchNotification,
  updateNotification,
} from "redux/actions/notification";
import NotificationRes from "types/res/notification/NotificationRes";
import { fetchShops } from "redux/actions/shop";
import { FormType } from "components/FormField";
import CreateNotificationReq from "types/req/notification/CreateNotificationReq";
import UpdateNotificationReq from "types/req/notification/UpdateNotificationReq";
import NotificationType from "../../types/enum/NotificationType";
import DateTimeUtils, { FORMAT_TYPE } from "../../utils/DateTimeUtils";
import EnumUtils from "../../utils/EnumUtils";
import Box from "@material-ui/core/Box";
import { MenuItem, TextField } from "@material-ui/core";
const Notification: React.FC = () => {
  const { t } = useTranslation("notification");
  const headers: { key: keyof NotificationRes; label: string }[] = [
    { key: "title", label: t("headers.title") },
    { key: "content", label: t("headers.content") },
    { key: "type", label: t("headers.type") },
    { key: "startDate", label: t("headers.startDate") },
    { key: "endDate", label: t("headers.endDate") },
    { key: "isIndefinite", label: t("headers.isIndefinite") },
    { key: "sort", label: t("headers.sort") },
    { key: "shops", label: t("headers.shops") },
  ];
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const shops = useSelector((state) => state.shop);
  const notification = useSelector((state) => state.notification);
  const [filterNotifications, setFilterNotifications] = useState<
    NotificationRes[]
  >([]);
  useEffect(() => {
    dispatch(fetchShops(companyId));
    dispatch(fetchNotification(companyId));
  }, [companyId, dispatch]);

  useEffect(() => {
    setFilterNotifications(notification);
  }, [notification]);

  const forms = [
    {
      label: t("fields.title"),
      key: "title",
      type: FormType.Text,
    },
    {
      label: t("fields.content"),
      key: "content",
      type: FormType.TextArea,
    },
    {
      label: t("fields.type"),
      key: "type",
      type: FormType.Select,
      options: Object.entries(NotificationType).map(([key]) => ({
        id: key,
        name: t(`dropdown.audience.${key}`),
      })),
    },
    {
      label: t("fields.startDate"),
      key: "startDate",
      type: FormType.Date,
    },
    {
      label: t("fields.endDate"),
      key: "endDate",
      type: FormType.Date,
    },
    {
      label: t("fields.isIndefinite"),
      key: "isIndefinite",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("values.off") },
        { id: "1", name: t("values.on") },
      ],
    },
    {
      label: t("fields.sort"),
      key: "sort",
      type: FormType.Number,
    },
    {
      label: t("fields.shop"),
      key: "shops",
      type: FormType.MultiOptions,
      options: shops.map((s) => ({ id: Number(s.shopId), name: s.name })),
      value: (val: any) => val.map((s: any) => s.shopId),
    },
  ];

  return (
    <>
      <Box display="flex" flexDirection="column">
        <Box margin={2}>
          <TextField
            label={t("filter.typeLabel")}
            select
            style={{ margin: "10px", width: "200px" }}
            onChange={(event) =>
              setFilterNotifications(
                notification.filter(
                  (n) =>
                    EnumUtils.mapToEnum(NotificationType, n.type) ===
                    event.target.value
                )
              )
            }
          >
            {Object.entries(NotificationType).map(([key, value]) => (
              <MenuItem value={value} key={value}>
                {t(`dropdown.audience.${key}`)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
      <NotificationTable
        title={t("title")}
        formId="notificationForm"
        forms={forms}
        rows={headers}
        data={filterNotifications}
        addFunc={(formData) => addNotification(companyId, formData)}
        updateFunc={(formData) => updateNotification(companyId, formData)}
        deleteFunc={(item) =>
          deleteNotification(companyId, {
            notificationId: item.notificationId,
          })
        }
        addType={CreateNotificationReq}
        updateType={UpdateNotificationReq}
        values={[
          (s) => s.title,
          (s) => s.content,
          (s) => {
            const audienceKey = Object.entries(NotificationType).find(
              ([, v]) => v === s.type
            )?.[0];
            return audienceKey
              ? t(`dropdown.audience.${audienceKey}`)
              : t("values.unset");
          },
          (s) =>
            s.startDate
              ? DateTimeUtils.toFormatAsLocalTimezone(
                  s.startDate,
                  FORMAT_TYPE.YEAR_DAY
                )
              : t("values.unset"),
          (s) =>
            s.endDate
              ? DateTimeUtils.toFormatAsLocalTimezone(
                  s.endDate,
                  FORMAT_TYPE.YEAR_DAY
                )
              : t("values.unset"),
          (s) => (s.isIndefinite ? t("values.on") : t("values.off")),
          (s) => s.sort || t("values.unset"),
          (s) =>
            s.shops
              .map(
                (shopRes: ShopRes) =>
                  shops.find((shop) => shop.shopId === shopRes.shopId)?.name
              )
              .join(",") || t("values.notAssigned"),
        ]}
      />
    </>
  );
};

const NotificationTable = styled<
  React.FC<
    CommonTableProps<
      NotificationRes,
      CreateNotificationReq,
      UpdateNotificationReq
    >
  >
>(CommonTable)`
  margin-top: 24px;
`;

export default Notification;
